import { type HTMLAttributes } from 'react';

export interface PageContainerProps extends HTMLAttributes<HTMLDivElement> {
  /** Centered max width of the page content. */
  maxWidth?: '4xl' | '5xl' | '6xl' | '240';
  /** Vertical spacing between direct children. `default` = `space-y-6`. */
  spacing?: 'default' | 'sm' | 'none';
}

const maxWidthStyles: Record<
  NonNullable<PageContainerProps['maxWidth']>,
  string
> = {
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '240': 'max-w-240',
};

const spacingStyles: Record<
  NonNullable<PageContainerProps['spacing']>,
  string
> = {
  none: '',
  sm: 'space-y-4',
  default: 'space-y-6',
};

/**
 * The app's standard page shell: centered, width-capped, with responsive
 * outer padding (`p-4` on mobile, `p-6` from `sm` up) and consistent vertical
 * rhythm between sections.
 */
export function PageContainer({
  maxWidth = '6xl',
  spacing = 'default',
  className = '',
  children,
  ...props
}: PageContainerProps) {
  const classes = [
    'mx-auto',
    maxWidthStyles[maxWidth],
    spacingStyles[spacing],
    'p-4 sm:p-6',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}
